import * as React from "react";

import { Box, makeStyles, Theme, Typography } from "@material-ui/core";
import clsx from "clsx";

import { MopButton } from "client/components";
import { useNormalizedBrandPaletteValues } from "client/hooks/useNormalizedBrandThemeValues";

const useStyles = makeStyles<Theme>((theme) => {
  const { gray40 } = useNormalizedBrandPaletteValues(theme);

  return {
    root: {
      display: "flex",
      alignItems: "stretch",
      marginBottom: theme.spacing(1),
      [theme.breakpoints.down("sm")]: {
        display: "initial",
      },
    },
    copyButton: {
      borderRadius: "0 12px 12px 0",
      padding: theme.spacing(1, 0),
      height: 48,
      width: 88,
    },
    copyButtonValue: {
      border: `1px solid #D8FDF1`, // magic color from mui
      borderRightStyle: "none",
      borderRadius: "12px 0 0 12px",
      color: gray40,
      width: 140,
    },
    copyButtonValueActive: {
      borderColor: theme.palette.primary.main,
    },
    copyHeader: {
      color: gray40,
      marginRight: theme.spacing(2),
      width: 100,
    },
    copyLabel: {
      fontSize: `${12 / 16}rem`,
    },
  };
});

type CopyKeyProps = {
  buttonText?: string;
  copyValue: string;
  disabled?: boolean;
  handleClick: () => void;
  header: string;
  isActive?: boolean;
};

export const CopyKey: React.FC<CopyKeyProps> = ({
  buttonText,
  copyValue,
  disabled = false,
  handleClick,
  header,
  isActive,
}) => {
  const [copied, setCopied] = React.useState(false);
  const [buttonTextState, setButtonTextState] = React.useState(
    buttonText ?? "Copy"
  );

  React.useEffect(() => {
    // if buttonText is passed in, use that
    if (!buttonText) {
      if (copied) {
        setTimeout(() => setCopied(false), 2000);
        setButtonTextState("Copied!");
      } else {
        setButtonTextState("Copy");
      }
    }
  }, [copied, buttonText]);

  const handleCopy = () => {
    handleClick();
    setCopied(true);
  };

  const classes = useStyles();
  return (
    <Box className={classes.root}>
      <Box alignItems="stretch" display="flex">
        <Box
          alignItems="center"
          className={clsx(
            classes.copyButtonValue,
            isActive && classes.copyButtonValueActive
          )}
          display="flex"
          flexDirection="column"
        >
          <Typography className={classes.copyLabel} variant="caption">
            {header}
          </Typography>
          <Typography
            color={disabled ? "inherit" : "textPrimary"}
            variant="caption"
          >
            {copyValue}
          </Typography>
        </Box>
        <MopButton
          className={clsx(classes.copyButton)}
          mopVariant={isActive ? "primary" : "default"}
          disabled={disabled}
          onClick={handleCopy}
        >
          {buttonTextState}
        </MopButton>
      </Box>
    </Box>
  );
};
